import {
  WfHeader,
  WfIconMiscAuthorizedAppsRegular,
  WfIconMiscBellRegular,
  WfIconMiscGlobeRegular,
  WfIconMiscLiveTrackRegular,
  WfIconMiscTrainingPreferencesRegular,
  WfIconMiscWorkoutProfilesRegular,
  WfIconNavigationChevronRightRegular,
  WfIconSportBrainRegular,
  WfMenuList,
  WfMenuListItem,
} from "@WahooFitness/redesignr";
import {
  postEvent,
  useConfigContext,
  useNativeMessagingContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { PLATFORM_ENUM } from "@WahooFitness/cloud-client-types";
import Bugsnag from "@bugsnag/js";
import { t } from "@lingui/macro";
import { Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ReactRouterLinkWrapper } from "../../MenuList/ReactRouterLinkWrapper";
import { openExternalLink } from "../../Membership/openExternalLink";

// The locales with a catalog; the I18nProvider shows English for any other locale.
const CATALOG_LOCALES = new Set(
  Object.keys(import.meta.glob("../../../locales/*.po")).map((path) =>
    path.replace(/^.*\/|\.po$/g, "")
  )
);

// Value of UIApplication.openSettingsURLString; opens this app's page in iOS Settings.
const IOS_APP_SETTINGS_URL = "app-settings:";

// WA Android answers whether it can open the system App languages page (Android 13+ only).
const CHECK_APP_LANGUAGE_SETTINGS_EVENT = "checkAppLanguageSettings";
// WA Android opens its page in the system App languages settings for this event.
const OPEN_APP_LANGUAGE_SETTINGS_EVENT = "openAppLanguageSettings";

// The offline MFE re-subscribes to event responses asynchronously after a listener is added,
// so the first answer can be missed; ask again until one arrives.
const CHECK_RETRY_MS = 500;
const CHECK_ATTEMPTS = 4;

/** Whether WA Android can open its App languages page. False until it answers, or off Android. */
function useAndroidAppLanguagesAvailable(isAndroid: boolean): boolean {
  const { addEventResponseListener, removeEventResponseListener } = useNativeMessagingContext();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!isAndroid) return;
    let answered = false;
    const listener = addEventResponseListener(
      CHECK_APP_LANGUAGE_SETTINGS_EVENT,
      ({ eventResponseData }) => {
        answered = true;
        setAvailable(eventResponseData?.available === true);
      }
    );
    let attempts = 0;
    const ask = () => {
      if (answered || attempts >= CHECK_ATTEMPTS) return;
      attempts += 1;
      postEvent(CHECK_APP_LANGUAGE_SETTINGS_EVENT);
    };
    ask();
    const retry = setInterval(ask, CHECK_RETRY_MS);
    return () => {
      clearInterval(retry);
      removeEventResponseListener(listener);
    };
  }, [isAndroid, addEventResponseListener, removeEventResponseListener]);

  return available;
}

/** The language the app is shown in, named in that language (e.g. "Français"). */
function getLanguageName(locale: string): string {
  const shown = CATALOG_LOCALES.has(locale) ? locale : "en";
  const name = new Intl.DisplayNames([shown], { type: "language" }).of(shown) ?? shown;
  return name.charAt(0).toLocaleUpperCase(shown) + name.slice(1);
}

function openIosLanguageSettings() {
  openExternalLink(IOS_APP_SETTINGS_URL).catch((error) => Bugsnag.notify(error));
}

function openAndroidLanguageSettings() {
  postEvent(OPEN_APP_LANGUAGE_SETTINGS_EVENT);
}

function SettingsSection() {
  const { locale, platform } = useConfigContext();
  const isIos = platform === PLATFORM_ENUM.ios;
  const androidAppLanguagesAvailable = useAndroidAppLanguagesAvailable(
    platform === PLATFORM_ENUM.android
  );
  const showLanguage = isIos || androidAppLanguagesAvailable;

  return (
    <>
      <WfHeader title={t`Settings`} hierarchy="H1" data-testid="settingsHeader" />
      <WfMenuList>
        <WfMenuListItem
          id="workout-profiles"
          content={t`Workout profiles`}
          variant="internalLink"
          href="/workout-profiles"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconMiscWorkoutProfilesRegular />}
          data-testid="settings.workoutProfiles"
        />
        <WfMenuListItem
          id="authorized-apps"
          content={t`Authorized apps`}
          variant="internalLink"
          href="/third-party-sharing"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconMiscAuthorizedAppsRegular />}
          data-testid="settings.sharing"
        />
        <WfMenuListItem
          id="notifications"
          content={t`Notifications`}
          variant="internalLink"
          href="/notifications"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconMiscBellRegular />}
          data-testid="settings.notifications"
        />
        <WfMenuListItem
          id="live-track"
          content={t`Live track`}
          variant="internalLink"
          href="/live-track"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconMiscLiveTrackRegular />}
          data-testid="settings.liveTrack"
        />
        <WfMenuListItem
          id="mental-recovery-preferences"
          content={t`Mental recovery preferences`}
          variant="internalLink"
          href="/mental-recovery-preferences"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconSportBrainRegular />}
          data-testid="settings.mentalRecoveryPreferences"
        />

        <WfMenuListItem
          id="workout-recommendations"
          content={t`Workout recommendations`}
          variant="internalLink"
          href="/workout-recommendations"
          linkComponent={ReactRouterLinkWrapper}
          startIcon={<WfIconMiscTrainingPreferencesRegular />}
          data-testid="settings.workoutRecommendations"
        />
        {showLanguage && (
          <WfMenuListItem
            id="language"
            content={t`Language`}
            variant="action"
            action={isIos ? openIosLanguageSettings : openAndroidLanguageSettings}
            startIcon={<WfIconMiscGlobeRegular />}
            secondaryRightComponentOverride={
              <Box display="flex" flexDirection="row" gap={2} alignItems="center">
                <Typography variant="prose-base">{getLanguageName(locale)}</Typography>
                <WfIconNavigationChevronRightRegular color="action" />
              </Box>
            }
            data-testid="settings.language"
          />
        )}
      </WfMenuList>
    </>
  );
}
export default SettingsSection;
